import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../api';
import MarkdownLegal from './MarkdownLegal';

/* Páginas legales del Canal (/condiciones y /privacidad). Reutilizan el sistema de
   TEXTOS LEGALES del CRM (legal-templates): tipo NORMATIVA (=condiciones de uso) y PRIVACIDAD,
   versionados y con flag "visible en canal".
   Orden de fuentes (público → admin → aviso):
     1) GET /public/legal/{tipo}          ← endpoint público que expone el doc publicado+visible_canal (lo crea backend/Guardián)
     2) GET /settings/legal-templates     ← fallback si hay sesión admin (para verlo en preview)
     3) aviso "documento pendiente de publicación"
   tipo: 'condiciones' → NORMATIVA · 'privacidad' → PRIVACIDAD */

const TEAL = '#2C6675';
const TIPO_DOC = { condiciones: 'NORMATIVA', privacidad: 'PRIVACIDAD' };
const TITULO = { condiciones: 'Condiciones Generales', privacidad: 'Política de Privacidad' };

/* ═══ CONTRATO ═══
   QUÉ HACE: enseña un texto legal del Canal. En página completa (rutas
     /condiciones y /privacidad) o EMBEBIDO dentro de otra pantalla (`inline`).
   NO SE PUEDE CAMBIAR:
     · El texto SIEMPRE sale del documento publicado (`legal_templates`). NUNCA
       se copia dentro de un componente: el día que Ann cambie la política, la
       pantalla seguiría enseñando la vieja y lo que firma la clienta dejaría de
       ser lo que dice el documento.
     · No crear un segundo componente para «enseñar lo mismo pero más pequeño».
       El modo `inline` existe justo para que no haya dos caminos.
   POR QUÉ EXISTE `inline`: 24-09-2026. En el alta, los textos legales se abrían
     en OTRA PESTAÑA y la clienta perdía el alta entera (el estado del embudo
     vive solo en memoria). Ann lo reprodujo en incógnito: «te saca de donde
     estás y ya nunca regresa». Le pasaba justo a quien se lee la política antes
     de aceptar. Ahora el texto va DENTRO de la propia pantalla del alta.
     ⚠️ Y de paso explica por qué «← Volver» no funcionaba: usa el historial del
     navegador, y una pestaña nueva no tiene historial al que volver.
   LO VIGILA: test_TESTAUTOPISTA_el_alta_no_saca_de_la_pagina.py */
export default function LegalCanal({ tipo = 'condiciones', inline = false }) {
  const docType = TIPO_DOC[tipo] || 'NORMATIVA';
  const [html, setHtml] = useState('');
  const [md, setMd] = useState('');
  const [version, setVersion] = useState('');
  const [estado, setEstado] = useState('cargando'); // cargando | ok | vacio

  useEffect(() => {
    let alive = true;
    (async () => {
      // 1) endpoint público (aún por crear en backend)
      try {
        const r = await axios.get(`${API}/public/legal/${tipo}`);
        if (alive && (r.data?.html || r.data?.content_markdown)) {
          setHtml(r.data.html || ''); setMd(r.data.content_markdown || '');
          setVersion(r.data.version || r.data.template_version || '');
          setEstado('ok'); return;
        }
      } catch (e) { /* sin endpoint público → probar admin */ }
      // 2) fallback admin (si hay sesión de staff, para verlo en preview)
      try {
        const r = await axios.get(`${API}/settings/legal-templates`);
        const list = Array.isArray(r.data) ? r.data : (r.data?.templates || []);
        // Prioriza el documento ONLINE del Canal (marcado "visible en canal"), NO el presencial.
        const doc = list.find(t => t.type === docType && t.visible_canal)
          || list.find(t => t.type === docType && t.is_print_default)
          || list.find(t => t.type === docType);
        if (doc) {
          const d = await axios.get(`${API}/settings/legal-templates/${doc.id}`);
          if (alive) {
            setHtml(d.data?.html_maquetado || '');
            setMd(d.data?.content_markdown || doc.content_markdown || '');
            setVersion(d.data?.version || doc.version || '');
            setEstado('ok'); return;
          }
        }
      } catch (e) { /* sin acceso */ }
      if (alive) setEstado('vacio');
    })();
    return () => { alive = false; };
  }, [tipo, docType]);

  // Cuerpo del documento. Es el MISMO en los dos modos: si se duplicara, el día
  // que cambie la forma de leer el texto habría que acordarse de los dos sitios.
  const cuerpo = (
    <>
      {estado === 'cargando' && <p style={{ color: '#5a7176', margin: 0 }}>Cargando el documento…</p>}
      {estado === 'ok' && html && <div className="onb-normativa" dangerouslySetInnerHTML={{ __html: html }} />}
      {/* ANTES: `whiteSpace: pre-wrap` → el documento salía EN CRUDO, con los
          símbolos del Markdown a la vista. Medido en producción el 26-09-2026:
          la tabla de la Privacidad (responsable · finalidades · derechos) se
          veía como una fila de palotes, y es justo lo que hay que leer antes
          de firmar. `MarkdownLegal` lo pinta con elementos de React: sin
          `dangerouslySetInnerHTML`, así que el documento no puede inyectar
          HTML, y lo que no entiende lo enseña tal cual — nunca se pierde texto. */}
      {estado === 'ok' && !html && md && <div className="onb-normativa"><MarkdownLegal texto={md} /></div>}
      {/* 🔴 NO CORTA NADA. Regla de Ann (24-09-2026): «que no se cargue la
          operativa, por Dios» → ningún arreglo puede impedir que alguien se dé
          de alta. Si el texto no se puede mostrar, se DICE con honestidad y se
          da una salida, pero NO se bloquea la firma. Se falla hacia «la clienta
          pasa», nunca hacia «la clienta se queda fuera». */}
      {estado === 'vacio' && (inline ? (
        <p style={{ color: '#8a6d3b', margin: 0 }}>
          No hemos podido cargar este documento ahora mismo. Puedes pedírnoslo en{' '}
          <a href="mailto:mpdonline@metodopilardominguez.com?subject=Texto%20legal%20del%20alta"
            style={{ color: '#077069', fontWeight: 700 }}>mpdonline@metodopilardominguez.com</a>{' '}
          y te lo enviamos.
        </p>
      ) : (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p style={{ color: '#8a6d3b', background: '#fbf4e6', border: '1px solid #efe0c0', borderRadius: 10, padding: '14px 16px', display: 'inline-block' }}>
            Este documento aún no está publicado. Se mostrará aquí en cuanto se publique en <b>Textos legales</b> y se marque como <b>visible en el canal</b>.
          </p>
        </div>
      ))}
    </>
  );

  // MODO EMBEBIDO: solo el texto, en letra pequeña, dentro de la pantalla que lo
  // pide. Sin cabecera, sin botón de volver y SIN NADA QUE NAVEGUE FUERA.
  if (inline) {
    return (
      <details style={{ marginTop: 10, borderTop: '1px solid #e2eeed', paddingTop: 10 }}>
        {/* Desplegable, NO navegación: abrir esto no mueve a nadie de la página.
            Y NO bloquea nada — Ann: «si le doy a aceptar sin leer nada, es mi
            problema». La obligación es que el texto ESTÉ, no que se lea: sin
            desplazamiento obligatorio, sin temporizador y sin puertas nuevas. */}
        <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#5a7176', listStyle: 'revert' }}>
          {TITULO[tipo]}{version ? ` · versión ${version}` : ''} — leer aquí
        </summary>
        <div style={{ maxHeight: 200, overflowY: 'auto', background: '#f7fbfa', border: '1px solid #e2eeed', borderRadius: 10, padding: '10px 12px', marginTop: 8, fontSize: 11.5, lineHeight: 1.55, color: '#4a5c61' }}>
          {cuerpo}
        </div>
      </details>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5faf9', fontFamily: "'Montserrat', system-ui, sans-serif", padding: '0 0 60px' }}>
      {/* Cabecera con el logo corporativo real */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e2eeed', padding: '18px clamp(18px,5vw,40px)', display: 'flex', alignItems: 'center', gap: 14 }}>
        <img src="/logo-pd.png" alt="Pilar Domínguez" style={{ height: 30, width: 'auto' }} />
        <span style={{ width: 1, height: 24, background: 'rgba(44,107,118,.3)' }} />
        <img src="/logo-mefir-trans.png" alt="MEFIR" style={{ height: 26, width: 'auto' }} />
      </div>

      <div style={{ maxWidth: 820, margin: '0 auto', padding: '32px clamp(18px,5vw,32px) 0' }}>
        <h1 style={{ fontSize: 'clamp(24px,4vw,34px)', fontWeight: 800, color: '#16323a', margin: '0 0 6px' }}>{TITULO[tipo]}</h1>
        <p style={{ fontSize: 13, color: '#7d9195', margin: '0 0 24px' }}>Canal Pilar Domínguez–MEFIR{version ? ` · versión ${version}` : ''}</p>

        <div style={{ background: '#fff', border: '1px solid #e2eeed', borderRadius: 16, padding: 'clamp(20px,4vw,36px)', fontSize: 15, color: '#2f4247', lineHeight: 1.7 }}>
          {cuerpo}
        </div>

        <p style={{ textAlign: 'center', marginTop: 26 }}>
          <button onClick={() => { window.history.length > 1 ? window.history.back() : (window.location.href = '/'); }} style={{ height: 42, padding: '0 22px', borderRadius: 10, border: '1px solid rgba(60,134,150,.35)', background: 'rgba(60,134,150,.10)', color: TEAL, fontWeight: 700, fontSize: 14.5, cursor: 'pointer', fontFamily: 'inherit' }}>← Volver</button>
        </p>
      </div>
    </div>
  );
}
